// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    assert,
    Curriculum,
    ErrorCode,
    isNonEmptyString,
    poll,
    VSCodeUrl,
    getProfileFileName,
    ProfileTheme,
    CoursePlatform,
} from '@microsoft/vscodeedu-api';
import { generateRandomUUID } from '@microsoft/vscodeedu-auth-util';
import { produce } from 'immer';
import React, { createContext, Dispatch, useCallback, useContext, useEffect, useReducer } from 'react';
import { injectIntl } from 'react-intl';
import { createSelector } from 'reselect';
import { apiEnvironment, ContextProps, getUserClient, getUserId, StoreState, useConfig, UserContext } from '.';
import { pollOperation, useAsyncRunner } from '../utilities/async-runner';
import { traceEvent } from '../utilities/diagnostics';
import { useTheme } from './theme-context';

// Curricula store.
export type CurriculumStore = Readonly<{
    state: StoreState;
    items: Curriculum[];
    lastError?: CurriculumStoreError;
}>;

// Error in curriculum store.
export type CurriculumStoreError = {
    code?: ErrorCode.MissingGitHubAccess;
    curriculumId: string;
};

// Curriculum store action.
export type CurriculumAction =
    | { type: 'Load' }
    | { type: 'LoadResult'; items: Curriculum[] | undefined }
    | { type: 'Reload' }
    | { type: 'LoadError' }
    | { type: 'Create'; curriculum: Partial<Curriculum> }
    | { type: 'CreateError'; error: CurriculumStoreError }
    | { type: 'Update'; curriculum: Curriculum }
    | { type: 'UpdateResult'; curriculum: Curriculum }
    | { type: 'UpdateError'; curriculumId: string; previous?: Curriculum }
    | { type: 'Publish'; curriculumId: string }
    | { type: 'Unpublish'; curriculumId: string }
    | { type: 'Delete'; curriculumId: string }
    | { type: 'DeleteResult'; curriculumId: string }
    | { type: 'LinkRepo'; repoName: string; platform: CoursePlatform };

// Curriculum context.
export const CurriculumContext = createContext<[state: CurriculumStore, reducer: Dispatch<CurriculumAction>]>(
    undefined!
);

// Curriculum context provider.
export const CurriculumContextProvider = injectIntl((props: ContextProps) => {
    const { intl } = props;
    const [userStore] = useContext(UserContext);
    const userId = getUserId(userStore);
    const client = getUserClient(userStore);
    const runner = useAsyncRunner();
    const [{ profileTheme }] = useTheme();
    const { vscodeVersion } = useConfig();

    // Loads user curriculum list.
    const listCurricula = useCallback(
        async () =>
            runner.run({
                task: async () => {
                    if (client) {
                        const data = await client.listUserCurricula('me');
                        dispatch({ type: 'LoadResult', items: data });
                    }
                },
                onError: () => {
                    dispatch({ type: 'LoadError' });
                },
                errorMessage: intl.formatMessage({
                    description: 'Error message for an async operation.',
                    defaultMessage: 'An error occurred while loading activities.',
                }),
            }),
        [runner, intl, client]
    );

    // Respond to user change by reloading the data.
    useEffect(() => dispatch({ type: 'Reload' }), [userId]);

    // Creates a new curriculum.
    const createCurriculum = useCallback(
        async (curriculum: Curriculum & { curriculumId: string }) => {
            const { title, curriculumId } = curriculum;
            return runner.run({
                task: async () => {
                    if (client) {
                        const { operationId } = await client.createUserCurriculum('me', curriculumId, curriculum);
                        assert.isDefined(operationId);

                        const data = await poll(async () => {
                            const data = await client.getOperation('me', operationId);
                            return data.status === 'succeeded' || data.status === 'failed' ? data : false;
                        });

                        if (data.status === 'succeeded') {
                            const curriculum = await client.getUserCurriculum('me', curriculumId);
                            const vscodeUrl = getOpenCurriculumUrl(
                                curriculum.curriculumId,
                                curriculum.repoName,
                                userId,
                                profileTheme,
                                vscodeVersion
                            );

                            if (isNonEmptyString(vscodeUrl)) {
                                window.location.href = vscodeUrl;
                            }

                            dispatch({ type: 'UpdateResult', curriculum });
                        } else if (data.error?.code === ErrorCode.MissingGitHubAccess) {
                            dispatch({
                                type: 'CreateError',
                                error: { code: ErrorCode.MissingGitHubAccess, curriculumId },
                            });
                        } else {
                            throw new Error(data.error?.message);
                        }
                    }
                },
                onError: () => {
                    dispatch({ type: 'CreateError', error: { curriculumId } });
                },
                progressMessage: intl.formatMessage(
                    {
                        description: 'Progress message for an async operation.',
                        defaultMessage: 'Creating activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                successMessage: intl.formatMessage(
                    {
                        description: 'Success message for an async operation.',
                        defaultMessage: 'Successfully created activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                errorMessage: intl.formatMessage(
                    {
                        description: 'Error message for an async operation.',
                        defaultMessage: 'An error occurred while creating activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
            });
        },
        [client, intl, profileTheme, runner, userId, vscodeVersion]
    );

    // Updates a curriculum.
    const updateCurriculum = useCallback(
        (curriculum: Curriculum, previous: Curriculum) => {
            const { curriculumId, title } = curriculum;
            runner.run({
                task: async () => {
                    if (client) {
                        await client.createOrUpdateUserCurriculum('me', curriculumId, { body: curriculum });
                    }
                },
                onError: () => {
                    dispatch({ type: 'UpdateError', curriculumId, previous });
                },
                progressMessage: intl.formatMessage(
                    {
                        description: 'Progress message for an async operation.',
                        defaultMessage: 'Updating activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                successMessage: intl.formatMessage(
                    {
                        description: 'Success message for an async operation.',
                        defaultMessage: 'Successfully updated activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                errorMessage: intl.formatMessage(
                    {
                        description: 'Error message for an async operation.',
                        defaultMessage: 'An error occurred while updating activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
            });
        },
        [client, intl, runner]
    );

    // Publishes a curriculum.
    const publishCurriculum = useCallback(
        (curriculum: Curriculum) => {
            const { curriculumId, title } = curriculum;
            runner.run({
                task: async () => {
                    if (client) {
                        await pollOperation(client, async () => client.publishUserCurriculum('me', curriculumId));
                        const curriculum = await client.getUserCurriculum('me', curriculumId);
                        dispatch({ type: 'UpdateResult', curriculum });
                    }
                },
                onError: () => {
                    dispatch({ type: 'UpdateError', curriculumId, previous: curriculum });
                },
                progressMessage: intl.formatMessage(
                    {
                        description: 'Progress message for an async operation.',
                        defaultMessage: 'Publishing activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                successMessage: intl.formatMessage(
                    {
                        description: 'Success message for an async operation.',
                        defaultMessage: 'Successfully published activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                errorMessage: intl.formatMessage(
                    {
                        description: 'Error message for an async operation.',
                        defaultMessage: 'An error occurred while publishing activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
            });
        },
        [client, intl, runner]
    );

    // Unpublishes a curriculum.
    const unpublishCurriculum = useCallback(
        (curriculum: Curriculum) => {
            const { curriculumId, title } = curriculum;
            runner.run({
                task: async () => {
                    if (client) {
                        await pollOperation(client, async () => client.unpublishUserCurriculum('me', curriculumId));
                        const curriculum = await client.getUserCurriculum('me', curriculumId);
                        dispatch({ type: 'UpdateResult', curriculum });
                    }
                },
                onError: () => {
                    dispatch({ type: 'UpdateError', curriculumId, previous: curriculum });
                },
                progressMessage: intl.formatMessage(
                    {
                        description: 'Progress message for an async operation.',
                        defaultMessage: 'Unpublishing activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                successMessage: intl.formatMessage(
                    {
                        description: 'Success message for an async operation.',
                        defaultMessage: 'Successfully unpublished activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                errorMessage: intl.formatMessage(
                    {
                        description: 'Error message for an async operation.',
                        defaultMessage: 'An error occurred while unpublishing activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
            });
        },
        [client, intl, runner]
    );

    // Deletes a curriculum.
    const deleteCurriculum = useCallback(
        (curriculum: Curriculum) => {
            const { curriculumId, title } = curriculum;
            runner.run({
                task: async () => {
                    if (client) {
                        await pollOperation(client, async () => client.deleteUserCurriculum('me', curriculumId));
                        dispatch({ type: 'DeleteResult', curriculumId });
                    }
                },
                onError: () => {
                    dispatch({ type: 'UpdateError', curriculumId, previous: curriculum });
                },
                progressMessage: intl.formatMessage(
                    {
                        description: 'Progress message for an async operation.',
                        defaultMessage: 'Deleting activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                successMessage: intl.formatMessage(
                    {
                        description: 'Success message for an async operation.',
                        defaultMessage: 'Successfully deleted activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
                errorMessage: intl.formatMessage(
                    {
                        description: 'Error message for an async operation.',
                        defaultMessage: 'An error occurred while deleting activity {ACTIVITY_TITLE}.',
                    },
                    { ACTIVITY_TITLE: title }
                ),
            });
        },
        [client, intl, runner]
    );

    const linkRepo = useCallback(
        (repoName: string, platform: CoursePlatform) => {
            runner.run({
                task: async () => {
                    if (client) {
                        await pollOperation(client, async () =>
                            client.linkUserCurriculum('me', repoName, {
                                platform,
                            })
                        );
                        listCurricula();
                    }
                },
                progressMessage: intl.formatMessage(
                    {
                        description: 'Progress message for an async operation.',
                        defaultMessage: 'Linking GitHub repository {REPO_NAME}.',
                    },
                    { REPO_NAME: repoName }
                ),
                errorMessage: intl.formatMessage(
                    {
                        description: 'Error message for an async operation.',
                        defaultMessage: 'An error occurred while linking GitHub repository {REPO_NAME}.',
                    },
                    { REPO_NAME: repoName }
                ),
            });
        },
        [client, intl, listCurricula, runner]
    );

    // Store action reducer - updates store state and kicks off async actions.
    const reducer = useCallback(
        (store: CurriculumStore, action: CurriculumAction) =>
            produce(store, (draft) => {
                traceEvent('curriculum-context.action', { type: action.type, state: draft.state });
                draft.lastError = undefined;
                switch (action.type) {
                    case 'Load':
                        if (draft.state === 'NotLoaded') {
                            listCurricula();
                            draft.state = 'Loading';
                        }
                        break;
                    case 'LoadResult':
                        if (action.items !== undefined) {
                            draft.items = action.items;
                            draft.state = 'Loaded';
                        } else {
                            draft.items = [];
                            draft.state = 'NotLoaded';
                        }
                        break;
                    case 'LoadError':
                        draft.state = 'NotLoaded';
                        break;
                    case 'Reload':
                        if (draft.state !== 'Loading' && draft.state !== 'NotLoaded') {
                            listCurricula();
                            draft.state = 'Loading';
                        }
                        break;
                    case 'Create': {
                        const item = {
                            ...action.curriculum,
                            type: 'course',
                            curriculumId: generateRandomUUID(),
                            state: 'creating',
                            created: new Date(),
                            lastUpdated: new Date(),
                        } as Curriculum;

                        createCurriculum(item);
                        draft.items.push(item);
                        break;
                    }
                    case 'CreateError': {
                        if (action.error?.code === ErrorCode.MissingGitHubAccess) {
                            draft.lastError = action.error;
                        } else {
                            const index = draft.items.findIndex((o) => o.curriculumId === action.error.curriculumId);
                            if (index >= 0) {
                                draft.items.splice(index, 1);
                            }
                        }
                        break;
                    }
                    case 'Update': {
                        const item = draft.items.find((o) => o.curriculumId === action.curriculum.curriculumId);
                        if (item) {
                            updateCurriculum(action.curriculum, { ...item });
                            item.state = 'updating';
                        }
                        break;
                    }
                    case 'UpdateResult': {
                        const index = draft.items.findIndex((o) => o.curriculumId === action.curriculum.curriculumId);
                        if (index >= 0) {
                            draft.items[index] = { ...action.curriculum };
                        }
                        break;
                    }
                    case 'UpdateError': {
                        const item = draft.items.find((o) => o.curriculumId === action.curriculumId);
                        if (item) {
                            item.state = action.previous?.state ?? 'draft';
                        }
                        break;
                    }
                    case 'Publish': {
                        const item = draft.items.find((o) => o.curriculumId === action.curriculumId);
                        if (item) {
                            publishCurriculum({ ...item });
                            item.state = 'publishing';
                        }
                        break;
                    }
                    case 'Unpublish': {
                        const item = draft.items.find((o) => o.curriculumId === action.curriculumId);
                        if (item) {
                            unpublishCurriculum({ ...item });
                            item.state = 'unpublishing';
                        }
                        break;
                    }
                    case 'Delete': {
                        const item = draft.items.find((o) => o.curriculumId === action.curriculumId);
                        if (item) {
                            deleteCurriculum({ ...item });
                            item.state = 'deleting';
                        }
                        break;
                    }
                    case 'DeleteResult': {
                        const index = draft.items.findIndex((o) => o.curriculumId === action.curriculumId);
                        if (index >= 0) {
                            draft.items.splice(index, 1);
                        }
                        break;
                    }
                    case 'LinkRepo':
                        linkRepo(action.repoName, action.platform);
                        break;
                    default:
                        assert.unreachable(action, 'action');
                }
            }),
        [
            linkRepo,
            listCurricula,
            createCurriculum,
            updateCurriculum,
            publishCurriculum,
            unpublishCurriculum,
            deleteCurriculum,
        ]
    );

    const [state, dispatch] = useReducer(reducer, { state: 'NotLoaded', items: [] });
    return <CurriculumContext.Provider value={[state, dispatch]}>{props.children}</CurriculumContext.Provider>;
});

// Use user curriculum context.
export const useCurricula = () => {
    const [store, dispatch] = useContext(CurriculumContext);
    useEffect(() => dispatch({ type: 'Load' }), [dispatch]);
    return [store, dispatch] as [typeof store, typeof dispatch];
};

// Selector to get a curriculum by ID.
export const getCurriculum = createSelector(
    (store: CurriculumStore) => store.items,
    (_: unknown, curriculumId: string) => curriculumId,
    (items, curriculumId) => items.find((o) => o.curriculumId === curriculumId)
);

// Selector to get whether curriculum store is currently loading.
export const getIsLoadingCurricula = createSelector(
    (store: CurriculumStore) => store.state,
    (state) => state !== 'Loaded'
);

// Returns a URL to open a curriculum in VS Code.
export function getOpenCurriculumUrl(
    curriculumId: string,
    repoName: string | undefined,
    userId: string | undefined,
    theme: ProfileTheme,
    vscodeVersion?: string
): string | undefined {
    return repoName !== undefined
        ? VSCodeUrl.forCurriculum(apiEnvironment, curriculumId, repoName, {
              accountId: userId,
              profile: getProfileFileName('curriculum', theme),
              vscodeVersion,
          }).toString()
        : undefined;
}
